/* ==========================================================================
   What went wrong, and what the operator can do about it.

   "Failed to load robots." is one sentence for nine different situations, and
   the recovery is different in every one of them. A console that collapses
   them tells an operator to keep pressing Retry against a 403.

   So: one taxonomy, nine classes, each with the sentence a triaging operator
   needs, and each with exactly one recovery. Two of those recoveries are
   "the console is already retrying, here is when" — which is only honest
   because the retry schedule below is the one the query client actually runs
   (see queryClient.ts). Nothing here guesses.

   Pure module: no React, no DOM. It is imported by the query client, so it
   has to stay that way.
   ========================================================================== */

export type FaultClass =
  /** Nothing answered: refused, DNS, CORS, the cable. No status exists. */
  | 'link'
  /** Something answered, eventually, but not inside the budget. */
  | 'timeout'
  /** 5xx — the API is up enough to say it is broken. */
  | 'server'
  /** 429 — we are the problem; slow down. */
  | 'rate'
  /** 401 — the token is gone or expired. */
  | 'auth'
  /** 403 — the token is fine and does not cover this. */
  | 'forbidden'
  /** 404 — the object is not there. Retrying cannot conjure it. */
  | 'missing'
  /** 400 / 409 / 422 — the request was wrong. Retrying repeats the mistake. */
  | 'rejected'
  /** Answered 2xx with a body we could not read. */
  | 'malformed'
  | 'unknown';

/**
 * The single thing to offer. Never two verbs on a fault line — an operator
 * mid-incident should not be choosing between recoveries.
 */
export type Recovery =
  /** The console is retrying on a schedule; offer "Retry now" as well. */
  | 'auto'
  /** No schedule will fix it, but a human re-read might. */
  | 'manual'
  /** Re-authenticate. */
  | 'signin'
  /** The object is gone; the only move is to leave. */
  | 'leave'
  /** Nothing the operator can do here. Say so rather than offering a verb. */
  | 'none';

export interface Fault {
  class: FaultClass;
  /** HTTP status, when something answered. Null when nothing did. */
  status: number | null;
  /** The error verbatim — `Failed to fetch`, `Unknown robot: nope-99`. */
  reason: string;
  recovery: Recovery;
  /** Does the query client retry this class on its own? */
  retried: boolean;
}

/* --------------------------------------------------------------------------
   Classification.

   The fetch layer throws `new Error('status 503')` for an HTTP failure and
   whatever `fetch` threw for a transport one, so this parses both shapes. It
   deliberately does NOT invent a status where none exists: `status: null` is
   the fact that nothing answered, and the UI prints it as such.
   -------------------------------------------------------------------------- */

const STATUS_RE = /\b(?:status|http)\s*[:=]?\s*(\d{3})\b/i;
const TRANSPORT_RE =
  /failed to fetch|networkerror|network request failed|load failed|err_(?:connection|network|name_not_resolved|address)|connection (?:refused|reset|closed)|fetch failed/i;

export function faultOf(error: unknown): Fault | null {
  if (error === null || error === undefined) return null;

  const err = error instanceof Error ? error : null;
  const reason = (err ? err.message : String(error)).trim() || 'Unknown error';
  const name = err?.name ?? '';

  // A status carried on the error object beats one parsed out of its message.
  const carried = (error as { status?: unknown }).status;
  const status =
    typeof carried === 'number' && carried >= 100 && carried <= 599
      ? carried
      : Number(STATUS_RE.exec(reason)?.[1] ?? NaN);

  if (Number.isFinite(status)) return fromStatus(status, reason);

  if (name === 'TimeoutError') return build('timeout', null, reason);
  // An AbortError is almost always us — a navigation, a superseded read. It is
  // a transport outcome, not a server one, and it self-heals on the next read.
  if (name === 'AbortError') return build('link', null, reason || 'Aborted');
  if (name === 'SyntaxError') return build('malformed', null, reason);
  if (error instanceof TypeError || TRANSPORT_RE.test(reason)) {
    return build('link', null, reason);
  }
  return build('unknown', null, reason);
}

function fromStatus(status: number, reason: string): Fault {
  if (status === 401) return build('auth', status, reason);
  if (status === 403) return build('forbidden', status, reason);
  if (status === 404 || status === 410) return build('missing', status, reason);
  if (status === 408) return build('timeout', status, reason);
  if (status === 429) return build('rate', status, reason);
  if (status >= 500) return build('server', status, reason);
  if (status >= 400) return build('rejected', status, reason);
  return build('malformed', status, reason);
}

/**
 * Is this fault about SUPPLY — the console's ability to read at all — or is
 * it about one object?
 *
 * The distinction decides whether the diagnostics rail appears. A 404 on a
 * deregistered robot is a complete, final answer from a healthy API and the
 * console is working perfectly; opening a supply ledger beside it would be
 * the screen crying outage over a robot that was simply deleted. A refused
 * connection, a 503 or a 429 are the API failing to supply the console, and
 * that is what the rail is for.
 */
export function isSupplyFault(cls: FaultClass): boolean {
  return cls !== 'missing' && cls !== 'forbidden' && cls !== 'rejected';
}

const RECOVERY: Record<FaultClass, Recovery> = {
  link: 'auto',
  timeout: 'auto',
  server: 'auto',
  rate: 'auto',
  auth: 'signin',
  forbidden: 'none',
  missing: 'leave',
  rejected: 'manual',
  malformed: 'manual',
  unknown: 'manual',
};

/* NO BROWSER STRING EVER REACHES AN OPERATOR.

   `fetch` rejects with `TypeError: Failed to fetch` in Chrome, `Load failed`
   in Safari and `NetworkError when attempting to fetch resource.` in Firefox.
   All three mean one thing — the request got no answer — and none of them is
   a sentence anyone should read on an operations console. They are also, by
   design, the LEAST informative failure the platform produces: the browser
   deliberately collapses DNS failure, refused connection, TLS error and CORS
   into the same string, so nothing is lost by replacing it.

   It matters because `reason` is what a panel prints when it wants the machine
   detail: `/commands`' stale strip ends `…that read, not now.  Failed to
   fetch`, and the ledger row's tooltip said the same. `no reply` is the word
   the fault line, the read tag and the supply ledger already use for exactly
   this state (see STATE_WORD), so normalising here is what makes those four
   places agree instead of describing one event in two vocabularies.

   Only the transport strings are rewritten. An API's own message — a 422's
   validation detail, a 409's conflict — is the most useful thing on the
   screen and is never touched. */
function housePhrase(cls: FaultClass, reason: string): string {
  if (cls !== 'link') return reason;
  return TRANSPORT_RE.test(reason) ? 'no reply from the API' : reason;
}

function build(cls: FaultClass, status: number | null, reason: string): Fault {
  return {
    class: cls,
    status,
    reason: housePhrase(cls, reason),
    recovery: RECOVERY[cls],
    retried: retriesFor(cls, status) > 0,
  };
}

/* --------------------------------------------------------------------------
   Retry policy — the behaviour behind the states.

   Two rules decide everything:

     1. Retry only what a retry could fix. A 404 does not become a 200 because
        it was asked three times; a 401 asked three times is three rejected
        tokens and three log lines for whoever is on call.
     2. Retry a transport failure hard, because that is the class that is
        usually a two-second blip on a site link — and it is the class where
        the operator is staring at a screen that has stopped moving.
   -------------------------------------------------------------------------- */

export function retriesFor(cls: FaultClass, status: number | null): number {
  switch (cls) {
    case 'link':
      return 3;
    case 'timeout':
      return 2;
    case 'server':
      // 502/503/504 are a restart or a rolling deploy and clear on their own.
      // A bare 500 is a bug in a handler and will still be there in 8 s.
      return status === 500 ? 1 : 3;
    case 'rate':
      return 2;
    case 'malformed':
    case 'unknown':
      return 1;
    default:
      // auth, forbidden, missing, rejected — a retry repeats the mistake.
      return 0;
  }
}

/**
 * Backoff, in ms, for the nth retry (0-based): 500 · 1000 · 2000 · 4000,
 * capped at 8 s. A 429 starts at 2 s, because the whole point is to stop.
 *
 * Deliberately un-jittered. This console is one browser talking to one API,
 * not a thousand clients stampeding a service, so jitter buys nothing — and
 * it would make the countdown printed on the fault line a lie.
 */
export function backoffMs(attempt: number, cls: FaultClass = 'unknown'): number {
  const floor = cls === 'rate' ? 2_000 : 500;
  return Math.min(floor * 2 ** Math.max(0, attempt), 8_000);
}

/**
 * `retry` for the query client.
 *
 * TanStack calls this BEFORE it increments, so `failureCount` is the number of
 * retries already spent — 0 on the first failure. Verified against
 * query-core/retryer.js, which evaluates `retry(failureCount, error)` and only
 * then does `failureCount++`. `useRead` reads the query's `failureCount`
 * AFTER that increment, which is why `nextAttemptInMs` counts from
 * `failureCount - 1` and this counts from `failureCount`.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  const f = faultOf(error);
  if (f === null) return false;
  // `failureCount` is 0 on the first call, so this reads as "retries so far".
  return failureCount < retriesFor(f.class, f.status);
}

/** `retryDelay` for the query client. `attempt` is 0 on the first retry. */
export function retryDelay(attempt: number, error: unknown): number {
  return backoffMs(attempt, faultOf(error)?.class ?? 'unknown');
}

/**
 * When the console will next try, in ms from now. Null = never, on its own.
 *
 * Once the retries above are spent the query settles into its error state and
 * the only thing that will fetch again is the poll timer — so a polled source
 * counts down to its next tick, and an on-demand one honestly says it will
 * not try again.
 */
export function nextAttemptInMs(
  fault: Fault,
  failedAt: number,
  failureCount: number,
  everyMs: number | null,
  now: number,
): number | null {
  const budget = retriesFor(fault.class, fault.status);
  if (failureCount > 0 && failureCount < budget) {
    return Math.max(0, failedAt + backoffMs(failureCount - 1, fault.class) - now);
  }
  if (everyMs !== null && everyMs > 0 && fault.class !== 'auth' && fault.class !== 'forbidden') {
    return Math.max(0, failedAt + everyMs - now);
  }
  return null;
}

/* --------------------------------------------------------------------------
   Copy.

   One sentence, sentence case, no exclamation mark, no apology, no "Oops".
   It names what did not happen, not how the console feels about it. The
   endpoint, the status and the run length go in the mono detail line beside
   it, never inside the sentence.
   -------------------------------------------------------------------------- */

/**
 * The state of a source in two or three words. The panel's fault line and the
 * supply ledger both print it, so it lives here rather than in either of them:
 * `no reply` in a panel over `Failed to fetch` in the ledger two hundred pixels
 * below is one event described twice, and an operator has to work out that
 * they are the same thing.
 */
const STATE_WORD: Record<FaultClass, string> = {
  link: 'no reply',
  timeout: 'timed out',
  server: 'server fault',
  rate: 'rate-limited',
  auth: 'session expired',
  forbidden: 'not permitted',
  missing: 'not on record',
  rejected: 'rejected',
  malformed: 'unreadable body',
  unknown: 'read failed',
};

export function stateWord(cls: FaultClass): string {
  return STATE_WORD[cls];
}

/**
 * Which of the two fault colours a class wears. An expired session is not a
 * platform fault — the API is healthy and answering promptly — so it wears the
 * amber of "you cannot read this right now" rather than the red of "something
 * is broken", and the banner and every panel line agree because they both ask
 * this function.
 */
export function faultTone(cls: FaultClass): 'error' | 'warn' {
  return cls === 'auth' || cls === 'rate' ? 'warn' : 'error';
}

export interface FaultCopy {
  /** The sentence. Never more than ten words. */
  headline: string;
  /** The verb to offer, or null when there is nothing useful to press. */
  verb: string | null;
}

/**
 * @param subject What the read was for, as a noun the operator uses:
 *                `jetrover-08`, `the fleet register`, `this capture session`.
 */
export function faultCopy(fault: Fault, subject: string, hasData: boolean): FaultCopy {
  const copy = sentence(fault, subject, hasData);
  return { ...copy, headline: openWith(copy.headline) };
}

/**
 * Capitalise the sentence — unless it opens on a machine string. `jetrover-08`
 * is the robot's id, not a proper noun, and `Jetrover-08` is a different
 * string from the one printed everywhere else on the screen.
 */
function openWith(line: string): string {
  const first = line.split(/[\s.,]/, 1)[0] ?? '';
  if (!/^[a-z]+$/.test(first)) return line;
  return line.charAt(0).toUpperCase() + line.slice(1);
}

/**
 * The reason with any `status NNN` prefix removed, because the status is
 * printed as its own field and `503 · status 503` is a console repeating
 * itself at the moment an operator is reading fastest.
 */
export function reasonText(fault: Fault): string | null {
  const stripped = fault.reason.replace(/^\s*(?:status|http)\s*[:=]?\s*\d{3}\s*[:\-–]?\s*/i, '').trim();
  return stripped.length === 0 ? null : stripped;
}

function sentence(fault: Fault, subject: string, hasData: boolean): FaultCopy {
  switch (fault.class) {
    case 'link':
      return {
        headline: hasData
          ? `${subject} has stopped answering.`
          : `No reply from ${subject}.`,
        verb: 'Retry now',
      };
    case 'timeout':
      return { headline: `${subject} did not answer in time.`, verb: 'Retry now' };
    case 'server':
      return { headline: `${subject} answered ${fault.status ?? 500}.`, verb: 'Retry now' };
    case 'rate':
      return { headline: 'The API is rate-limiting this console.', verb: 'Retry now' };
    case 'auth':
      return { headline: 'The session has expired.', verb: 'Sign in again' };
    case 'forbidden':
      return { headline: `This account cannot read ${subject}.`, verb: null };
    case 'missing':
      return { headline: `${subject} is not on record.`, verb: null };
    case 'rejected':
      return { headline: 'The request was rejected.', verb: 'Retry now' };
    case 'malformed':
      return { headline: `${subject} answered with a body this console could not read.`, verb: 'Retry now' };
    default:
      return { headline: `The read of ${subject} failed.`, verb: 'Retry now' };
  }
}

/* --------------------------------------------------------------------------
   Absence copy.

   The other half of the vocabulary, in the same file as the fault copy for one
   reason: the console had FOUR sentences for one idea — "No robots on the
   roster." / "No robots registered." / "No commands match." / "No commands
   issued to this robot." — which is four different consoles as far as an
   operator scanning six panels is concerned. There is one place a sentence
   about an absence is written, and this is it.

   Six kinds, because an absence really is six different facts with six
   different next actions:

     nothing    it exists and has none of these yet
     filtered   there ARE rows; this filter hides them. Say so, print the
                filter, offer the way out
     never      the object has never produced this class of data at all
     awaiting   the platform does not carry it yet
     missing    the API answered 404. Same sentence `sentence()` prints above
     withheld   the API answered 403
   -------------------------------------------------------------------------- */

export type BlankKind = 'nothing' | 'filtered' | 'never' | 'awaiting' | 'missing' | 'withheld';

export interface BlankWords {
  /** The plural noun for what would be here: `robots`, `commands`, `scans`. */
  noun?: string | undefined;
  /**
   * Where they would be, as a past participle or a prepositional phrase:
   * `registered`, `issued to this robot`, `in this window`. Omit for a bare
   * "No robots."
   */
  where?: string | undefined;
  /** The object, for `never` / `missing` / `withheld`: `jetrover-08`. */
  subject?: string | undefined;
  /** What it has never produced, for `never`: `telemetry`, `a capture`. */
  produced?: string | undefined;
}

/** The house sentence for an absence. The only place these are written. */
export function blankLine(kind: BlankKind, words: BlankWords = {}): string {
  const noun = words.noun ?? 'rows';
  const subject = words.subject ?? 'this object';
  switch (kind) {
    case 'filtered':
      return `No ${noun} match this filter.`;
    case 'never':
      return `${subject} has never reported ${words.produced ?? noun}.`;
    case 'awaiting':
      return `${openWith(noun)} is not carried on this platform yet.`;
    case 'missing':
      // The same sentence a 404 gets on a panel that still has figures.
      return `${subject} is not on record.`;
    case 'withheld':
      return `This account cannot read ${subject}.`;
    default:
      return words.where === undefined ? `No ${noun}.` : `No ${noun} ${words.where}.`;
  }
}
